import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { EyeOff, MessageCircleQuestion, Reply } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type DataTableColumnDef,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { AdminAuctionQuestion } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Tabs, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { AnswerQuestionDialog } from '../components/answer-question-dialog';
import { AuctionsNav } from '../components/auctions-nav';
import { ReasonDialog } from '../components/reason-dialog';
import { QuestionStatusBadge } from '../components/sale-status-badge';
import { useHideQuestion, useQuestions } from '../hooks/use-questions';
import { questionTab } from '../schemas/sale-list-search';
import { QUESTION_TABS, type QuestionTab } from '../schemas/sale-rules';

const ROUTE_ID = '/_authed/_active/s/$storeId/auctions/questions' as const;
const ROUTE_PATH = '/s/$storeId/auctions/questions' as const;

const asker = (row: AdminAuctionQuestion) =>
  row.bidderLabel ?? row.customerName ?? row.customerEmail;

function Questions() {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { editable, reason } = useStoreEditable({ permission: 'auctions.write' });
  const [answering, setAnswering] = useState<AdminAuctionQuestion | null>(null);
  const [hiding, setHiding] = useState<AdminAuctionQuestion | null>(null);
  const hide = useHideQuestion(store.id);
  const tab = questionTab(search);

  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange });
  const query = useQuestions(store.id, { ...list.params, status: tab });

  const columns = useMemo<Array<DataTableColumnDef<AdminAuctionQuestion>>>(
    () => [
      {
        id: 'question',
        header: t('questions.columns.question'),
        cell: ({ row }) => (
          <span className="flex min-w-0 flex-col gap-1">
            <span className="whitespace-normal">{row.original.question}</span>
            {row.original.answer ? (
              <span className="text-muted-foreground text-xs whitespace-normal">
                <span className="font-medium">{t('questions.answerPrefix')}</span>{' '}
                {row.original.answer}
              </span>
            ) : row.original.hiddenReason ? (
              <span className="text-muted-foreground text-xs">
                {t('questions.hiddenReason', { reason: row.original.hiddenReason })}
              </span>
            ) : null}
          </span>
        ),
        meta: { label: t('questions.columns.question'), lockVisibility: true },
        size: 420,
      },
      {
        id: 'lot',
        header: t('questions.columns.lot'),
        cell: ({ row }) => (
          <Link
            to="/s/$storeId/auctions/$auctionId"
            params={{ storeId: store.id, auctionId: row.original.lot.auctionId }}
            className="font-medium whitespace-normal hover:underline"
          >
            {row.original.lot.title}
          </Link>
        ),
        meta: { label: t('questions.columns.lot') },
        size: 220,
      },
      {
        id: 'asker',
        header: t('questions.columns.asker'),
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span>{asker(row.original)}</span>
            <span className="text-muted-foreground text-xs">{row.original.customerEmail}</span>
          </span>
        ),
        meta: { label: t('questions.columns.asker') },
        size: 200,
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <QuestionStatusBadge status={row.original.status} />,
        size: 110,
      },
      {
        id: 'asked',
        header: t('questions.columns.asked'),
        cell: ({ row }) => (
          <span title={format.utc(row.original.createdAt)}>
            {format.relative(row.original.createdAt)}
          </span>
        ),
        meta: { label: t('questions.columns.asked') },
        size: 140,
      },
      ...(editable
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">{t('questions.columns.actions')}</span>,
              cell: ({ row }: { row: { original: AdminAuctionQuestion } }) =>
                row.original.status === 'pending' ? (
                  <span className="flex justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setAnswering(row.original)}
                    >
                      <Reply aria-hidden />
                      {t('questions.actions.answer')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={() => setHiding(row.original)}
                    >
                      <EyeOff aria-hidden />
                      {t('questions.actions.hide')}
                    </Button>
                  </span>
                ) : null,
              meta: { label: t('questions.columns.actions'), align: 'end' as const },
              size: 180,
            },
          ]
        : []),
    ],
    [editable, format, store.id, t],
  );

  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    getRowId: (row) => row.id,
    tableId: 'auction-questions',
  });
  const isEmpty = query.isSuccess && query.data.items.length === 0 && !list.cursor;

  return (
    <PageContent>
      <PageHeader title={t('questions.title')} description={t('questions.description')} />
      {reason === 'closed' ? <ReadOnlyBanner reason="closed" /> : null}
      <AuctionsNav storeId={store.id} />
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              status: value === 'pending' ? undefined : (value as QuestionTab),
              cursor: undefined,
            }),
          })
        }
        className="mb-4"
      >
        <TabsList aria-label={t('questions.tabs.label')}>
          {QUESTION_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`questions.status.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {isEmpty ? (
        <EmptyState
          icon={MessageCircleQuestion}
          title={t(`questions.empty.${tab}.title`)}
          description={t(`questions.empty.${tab}.description`)}
        />
      ) : (
        <DataTable
          table={table}
          toolbar={false}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          getRowLabel={(row) => row.question}
          resourceName={{
            singular: t('questions.resource.one'),
            plural: t('questions.resource.other'),
          }}
          emptyState={{ title: t('noMatches') }}
        />
      )}
      {editable ? (
        <>
          <AnswerQuestionDialog
            storeId={store.id}
            question={answering}
            onOpenChange={(open) => {
              if (!open) setAnswering(null);
            }}
          />
          <ReasonDialog
            open={hiding !== null}
            onOpenChange={(open) => {
              if (!open) setHiding(null);
            }}
            title={t('questions.hide.title')}
            description={t('questions.hide.description', { bidder: hiding ? asker(hiding) : '' })}
            label={t('questions.hide.reason')}
            hint={t('questions.hide.hint')}
            confirmLabel={t('questions.hide.confirm')}
            pending={hide.isPending}
            destructive={false}
            onConfirm={(text) =>
              hiding ? hide.mutateAsync({ id: hiding.id, reason: text }) : Promise.resolve()
            }
          />
        </>
      ) : null}
    </PageContent>
  );
}

export function AuctionQuestionsPage() {
  return (
    <FeatureErrorBoundary name="auctions.questions">
      <Questions />
    </FeatureErrorBoundary>
  );
}
